import { z } from 'zod';
import { idSchema, ntnSchema, paginationSchema, phoneSchema } from './common.js';
import { ALL_MODULES, BUSINESS_MODULES, BUSINESS_PROFILES, SHOP_CATEGORIES, SHOP_KINDS, USER_SCOPES } from '../constants.js';
import { passwordSchema } from './auth.js';
/* -------------------------------------------------------------------------- */
/* Business (Owner portal)                                                     */
/* -------------------------------------------------------------------------- */
export const businessCodeSchema = z
    .string()
    .trim()
    .toUpperCase()
    .min(2)
    .max(16)
    .regex(/^[A-Z0-9_]+$/, 'Use letters, digits and underscores only');
export const createBusinessSchema = z.object({
    code: businessCodeSchema,
    name: z.string().trim().min(2).max(160),
    legalName: z.string().trim().max(200).optional(),
    profile: z.enum(BUSINESS_PROFILES).default('SIMPLE'),
    phone: phoneSchema.optional(),
    email: z.string().trim().email().max(200).optional(),
    address: z.string().trim().max(400).optional(),
    city: z.string().trim().max(80).optional(),
    ntn: ntnSchema.optional(),
    strn: z.string().trim().max(32).optional(),
    /**
     * README.md Part E2 (Phase 28) — which package this business is sold on.
     * Omitted means "the default package"; `modulesEnabled` below is then
     * derived from it and the hand-typed list is ignored.
     */
    packageId: z.string().trim().min(1).max(64).optional(),
    planName: z.string().trim().max(64).default('starter'),
    shopLimit: z.number().int().min(1).max(500).default(1),
    userLimit: z.number().int().min(1).max(5_000).default(5),
    /**
     * Defaults to EVERY module: there is one plan, and a business created with
     * an empty list silently shows a "Plan" lock on half the settings screen.
     */
    modulesEnabled: z.array(z.enum(BUSINESS_MODULES)).default(ALL_MODULES),
    licenseExpiresAt: z.coerce.date().optional(),
    // The business's first shop and super-admin are created in the same call —
    // an Owner should never be able to leave a business unusable.
    firstShop: z.object({
        code: z.string().trim().toUpperCase().min(1).max(8).default('S01'),
        name: z.string().trim().min(1).max(120),
        city: z.string().trim().max(80).optional(),
        address: z.string().trim().max(400).optional(),
    }),
    superAdmin: z.object({
        username: z.string().trim().min(3).max(64).regex(/^[a-zA-Z0-9._-]+$/),
        fullName: z.string().trim().min(2).max(120),
        email: z.string().trim().email().max(200).optional(),
        phone: phoneSchema.optional(),
        /** Omit to have a temporary password generated and returned once. */
        password: passwordSchema.optional(),
    }),
});
export const updateBusinessSchema = createBusinessSchema
    .omit({ code: true, firstShop: true, superAdmin: true })
    .partial();
export const businessLicenseSchema = z.object({
    planName: z.string().trim().max(64).optional(),
    shopLimit: z.number().int().min(1).max(500).optional(),
    userLimit: z.number().int().min(1).max(5_000).optional(),
    modulesEnabled: z.array(z.enum(BUSINESS_MODULES)).optional(),
    licenseExpiresAt: z.coerce.date().nullable().optional(),
    graceDays: z.number().int().min(0).max(365).optional(),
    subscriptionExpired: z.boolean().optional(),
    isActive: z.boolean().optional(),
});
export const listBusinessesSchema = paginationSchema.extend({
    q: z.string().trim().max(120).optional(),
    isActive: z.coerce.boolean().optional(),
});
/* -------------------------------------------------------------------------- */
/* Shop                                                                        */
/* -------------------------------------------------------------------------- */
export const createShopSchema = z.object({
    code: z
        .string()
        .trim()
        .toUpperCase()
        .min(1)
        .max(8)
        .regex(/^[A-Z0-9]+$/, 'Use letters and digits only')
        .describe('Prefixes every human-visible number this shop mints, e.g. S01-000123'),
    name: z.string().trim().min(1).max(120),
    phone: phoneSchema.optional(),
    address: z.string().trim().max(400).optional(),
    city: z.string().trim().max(80).optional(),
    areaId: idSchema.optional(),
    category: z.enum(SHOP_CATEGORIES).optional(),
    portalEnabled: z.boolean().optional(),
    portalSlug: z
        .string()
        .trim()
        .toLowerCase()
        .regex(/^[a-z0-9](?:[a-z0-9-]{1,58}[a-z0-9])?$/, 'Use 3-60 lowercase letters, digits and dashes')
        .nullable()
        .optional(),
    portalDescription: z.string().trim().max(300).nullable().optional(),
    acceptingOrders: z.boolean().optional(),
    pickupEnabled: z.boolean().optional(),
    deliveryEnabled: z.boolean().optional(),
    latitude: z.number().min(-90).max(90).optional(),
    longitude: z.number().min(-180).max(180).optional(),
    dayCutoverTime: z
        .string()
        .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use HH:mm')
        .default('00:00'),
    /** README.md Phase 24 — stock-only WAREHOUSE vs a normal selling RETAIL shop. */
    kind: z.enum(SHOP_KINDS).default('RETAIL'),
});
export const updateShopSchema = createShopSchema.partial().omit({ code: true });
/** Owner-portal-only (README.md Phase 24) — the subtractive per-shop module carve-out. */
export const shopModulesOverrideSchema = z.object({
    modulesDisabled: z.array(z.enum(BUSINESS_MODULES)),
});
export const shopPortalSchema = z.object({
    portalEnabled: z.boolean().optional(),
    portalSlug: z
        .string()
        .trim()
        .toLowerCase()
        .min(3)
        .max(120)
        .regex(/^[a-z0-9-]+$/, 'Use lowercase letters, digits and hyphens')
        .optional(),
    portalDescription: z.string().trim().max(600).optional(),
    acceptingOrders: z.boolean().optional(),
    pickupEnabled: z.boolean().optional(),
    deliveryEnabled: z.boolean().optional(),
    deliveryRadiusM: z.number().int().min(0).max(100_000).optional(),
    deliveryFeePaisa: z.number().int().min(0).optional(),
    freeDeliveryAbovePaisa: z.number().int().min(0).optional(),
    minOrderPaisa: z.number().int().min(0).optional(),
    prepTimeMinutes: z.number().int().min(0).max(1_440).optional(),
    openingHours: z
        .array(z.object({
        day: z.number().int().min(0).max(6),
        open: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
        close: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
    }))
        .optional(),
});
/* -------------------------------------------------------------------------- */
/* Users                                                                       */
/* -------------------------------------------------------------------------- */
export const createUserSchema = z
    .object({
    username: z.string().trim().min(3).max(64).regex(/^[a-zA-Z0-9._-]+$/),
    fullName: z.string().trim().min(2).max(120),
    // Till/PIN users often have no email — blank is valid, not just omitted.
    email: z.preprocess((v) => (v === '' ? undefined : v), z.string().trim().email().max(200).optional()),
    phone: phoneSchema.optional(),
    password: passwordSchema.optional(),
    scope: z.enum(USER_SCOPES).default('SHOP'),
    allShops: z.boolean().default(false),
    shopIds: z.array(idSchema).default([]),
    defaultShopId: idSchema.optional(),
    roleIds: z.array(idSchema).min(1, 'Assign at least one role'),
    mustChangePassword: z.boolean().default(true),
    isActive: z.boolean().default(true),
})
    .refine((v) => v.allShops || v.shopIds.length > 0, {
    message: 'Assign at least one shop, or grant access to all shops',
    path: ['shopIds'],
})
    .refine((v) => !v.defaultShopId || v.allShops || v.shopIds.includes(v.defaultShopId), {
    message: 'The default shop must be one of the assigned shops',
    path: ['defaultShopId'],
});
export const updateUserSchema = z.object({
    fullName: z.string().trim().min(2).max(120).optional(),
    email: z.preprocess((v) => (v === '' ? null : v), z.string().trim().email().max(200).nullable().optional()),
    phone: phoneSchema.nullable().optional(),
    scope: z.enum(USER_SCOPES).optional(),
    allShops: z.boolean().optional(),
    shopIds: z.array(idSchema).optional(),
    defaultShopId: idSchema.nullable().optional(),
    roleIds: z.array(idSchema).min(1).optional(),
    isActive: z.boolean().optional(),
});
export const resetPasswordSchema = z.object({
    newPassword: passwordSchema.optional(),
    mustChangePassword: z.boolean().default(true),
});
export const listUsersSchema = paginationSchema.extend({
    q: z.string().trim().max(120).optional(),
    shopId: idSchema.optional(),
    isActive: z.coerce.boolean().optional(),
});
/* -------------------------------------------------------------------------- */
/* Roles                                                                       */
/* -------------------------------------------------------------------------- */
export const createRoleSchema = z.object({
    name: z.string().trim().min(2).max(64).regex(/^[A-Z0-9_]+$/, 'Use UPPER_SNAKE_CASE'),
    description: z.string().trim().max(300).optional(),
    permissions: z.array(z.string().min(1)).default([]),
    /** Clone the permission set of an existing role as a starting point. */
    cloneFromRoleId: idSchema.optional(),
});
export const updateRoleSchema = z.object({
    description: z.string().trim().max(300).optional(),
    permissions: z.array(z.string().min(1)).optional(),
});
export const userPermissionOverrideSchema = z.object({
    overrides: z
        .array(z.object({
        permission: z.string().min(1),
        effect: z.enum(['GRANT', 'DENY']),
        note: z.string().trim().max(300).optional(),
    }))
        .max(500),
});
/* -------------------------------------------------------------------------- */
/* Settings                                                                    */
/* -------------------------------------------------------------------------- */
export const settingsQuerySchema = z.object({
    /** Omit for the business-level values; pass a shop id for that shop's overrides. */
    shopId: idSchema.optional(),
    group: z.string().trim().max(40).optional(),
});
export const updateSettingsSchema = z.object({
    shopId: idSchema.optional(),
    values: z.record(z.string().min(1), z.unknown()),
});
export const clearSettingOverrideSchema = z.object({
    shopId: idSchema,
    keys: z.array(z.string().min(1)).min(1),
});
//# sourceMappingURL=tenancy.js.map